import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getRevenueCombined } from '@/modules/Report/components/Revenue/services'
import { RevenueCombinedSummary } from '@/modules/Report/components/Revenue/types'
import { formatNumeral } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import dayjs from 'dayjs'
import {
    TrendingUp,
    DollarSign,
    CheckCircle,
    Clock,
    PieChart,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'
import {
    PieChart as RechartsPieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
} from 'recharts'

const COLORS = {
    actual: '#10b981', // green
    projected: '#f59e0b', // amber
    paid: '#14b8a6', // teal
    unpaid: '#ef4444', // red
}

const RevenueAllocationWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()

    const today = dayjs()
    const gte = today.startOf('quarter').format('YYYY-MM')
    const lte = today.endOf('quarter').format('YYYY-MM')

    // Fetch revenue summary for current quarter
    const { data, isLoading } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BI_REVENUE_ALLOCATIONS,
            'dashboard-summary',
            gte,
            lte,
        ],
        queryFn: () =>
            getRevenueCombined({
                gte,
                lte,
                page: 1,
                page_size: 1,
            }),
    })

    const summary: RevenueCombinedSummary | undefined = data?.summary

    const statistics = {
        totalRevenue: summary?.total_allocated_amount || 0,
        actualRevenue: summary?.total_actual_amount || 0,
        projectedRevenue: summary?.total_projected_amount || 0,
        paidAmount: summary?.total_paid_amount || 0,
        unpaidAmount: summary?.total_unpaid_amount || 0,
    }

    // Pie chart data for revenue type breakdown
    const revenueTypeData = [
        {
            name: t('finance.actual_revenue', 'Thực tế'),
            value: statistics.actualRevenue,
            color: COLORS.actual,
        },
        {
            name: t('finance.projected_revenue', 'Dự kiến'),
            value: statistics.projectedRevenue,
            color: COLORS.projected,
        },
    ].filter((item) => item.value > 0)

    // Pie chart data for payment status breakdown
    const paymentStatusData = [
        {
            name: t('finance.paid', 'Đã thanh toán'),
            value: statistics.paidAmount,
            color: COLORS.paid,
        },
        {
            name: t('finance.unpaid', 'Chưa thanh toán'),
            value: statistics.unpaidAmount,
            color: COLORS.unpaid,
        },
    ].filter((item) => item.value > 0)

    const handleViewAll = () => {
        navigate(generatePathDatacentral(paths.reportRevenue, {}))
    }

    const formatValue = (value: number) => {
        if (value >= 1000000000) {
            return `${formatNumeral(value / 1000000000, 1)}B`
        }
        if (value >= 1000000) {
            return `${formatNumeral(value / 1000000, 1)}M`
        }
        return formatNumeral(value, 0)
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload
            return (
                <div className="bg-white p-2 shadow-lg rounded border text-xs">
                    <p className="font-medium">{data.name}</p>
                    <p className="text-gray-600">
                        {formatNumeral(data.value, 0)} VND
                    </p>
                </div>
            )
        }
        return null
    }

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Title */}
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-500" />
                        <Typogaphy className="font-semibold text-primary">
                            {t(
                                'finance.revenue_allocation',
                                'Phân bổ doanh thu'
                            )}
                        </Typogaphy>
                    </div>
                    <span className="text-xs text-gray-500">
                        Q{today.quarter()} {today.year()}
                    </span>
                </div>

                {isLoading ? (
                    <div className="space-y-3 flex-1">
                        <Skeleton className="h-20 rounded-lg" />
                        <div className="grid grid-cols-2 gap-2">
                            <Skeleton className="h-32 rounded-lg" />
                            <Skeleton className="h-32 rounded-lg" />
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Total Revenue Card */}
                        <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-3 mb-3 flex-shrink-0"
                            data-tour="revenue-alloc-total"
                        >
                            <div className="flex items-center justify-between text-white">
                                <div>
                                    <div className="text-xs opacity-80">
                                        {t(
                                            'finance.total_revenue',
                                            'Tổng doanh thu'
                                        )}
                                    </div>
                                    <div className="text-xl font-bold">
                                        {formatValue(statistics.totalRevenue)}
                                    </div>
                                </div>
                                <DollarSign className="w-8 h-8 opacity-50" />
                            </div>
                        </div>

                        {/* Charts Row */}
                        <div className="grid grid-cols-2 gap-2 flex-1 min-h-0">
                            {/* Revenue Type Chart */}
                            <div
                                className="bg-gray-50 rounded-lg p-2"
                                data-tour="revenue-alloc-by-type"
                            >
                                <div className="text-[10px] font-medium text-gray-600 mb-1 text-center">
                                    {t('finance.by_type', 'Theo loại')}
                                </div>
                                {revenueTypeData.length > 0 ? (
                                    <div className="h-24">
                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >
                                            <RechartsPieChart>
                                                <Pie
                                                    data={revenueTypeData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={20}
                                                    outerRadius={35}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                >
                                                    {revenueTypeData.map(
                                                        (entry, index) => (
                                                            <Cell
                                                                key={`cell-${index}`}
                                                                fill={
                                                                    entry.color
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </Pie>
                                                <Tooltip
                                                    content={<CustomTooltip />}
                                                />
                                            </RechartsPieChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    <div className="h-24 flex items-center justify-center">
                                        <PieChart className="w-8 h-8 text-gray-300" />
                                    </div>
                                )}
                                <div className="flex justify-center gap-2 mt-1">
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-green-500" />
                                        <span className="text-[9px] text-gray-600">
                                            {formatValue(
                                                statistics.actualRevenue
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-amber-500" />
                                        <span className="text-[9px] text-gray-600">
                                            {formatValue(
                                                statistics.projectedRevenue
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Status Chart */}
                            <div
                                className="bg-gray-50 rounded-lg p-2"
                                data-tour="revenue-alloc-by-payment"
                            >
                                <div className="text-[10px] font-medium text-gray-600 mb-1 text-center">
                                    {t('finance.by_payment', 'Theo thanh toán')}
                                </div>
                                {paymentStatusData.length > 0 ? (
                                    <div className="h-24">
                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >
                                            <RechartsPieChart>
                                                <Pie
                                                    data={paymentStatusData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={20}
                                                    outerRadius={35}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                >
                                                    {paymentStatusData.map(
                                                        (entry, index) => (
                                                            <Cell
                                                                key={`cell-${index}`}
                                                                fill={
                                                                    entry.color
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </Pie>
                                                <Tooltip
                                                    content={<CustomTooltip />}
                                                />
                                            </RechartsPieChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    <div className="h-24 flex items-center justify-center">
                                        <PieChart className="w-8 h-8 text-gray-300" />
                                    </div>
                                )}
                                <div className="flex justify-center gap-2 mt-1">
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-teal-500" />
                                        <span className="text-[9px] text-gray-600">
                                            {formatValue(statistics.paidAmount)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-red-500" />
                                        <span className="text-[9px] text-gray-600">
                                            {formatValue(
                                                statistics.unpaidAmount
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stats Row */}
                        <div
                            className="grid grid-cols-2 gap-2 mt-2 flex-shrink-0"
                            data-tour="revenue-alloc-stats"
                        >
                            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <div>
                                    <div className="text-xs font-bold text-green-600">
                                        {summary?.total_paid_records || 0}
                                    </div>
                                    <div className="text-[9px] text-green-600">
                                        {t('finance.paid_records', 'Đã TT')}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
                                <Clock className="w-4 h-4 text-red-600" />
                                <div>
                                    <div className="text-xs font-bold text-red-600">
                                        {summary?.total_unpaid_records || 0}
                                    </div>
                                    <div className="text-[9px] text-red-600">
                                        {t('finance.unpaid_records', 'Chưa TT')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* View All */}
                        <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                            <button
                                onClick={handleViewAll}
                                className="text-xs text-primary hover:underline"
                            >
                                {t('widgets.view_all')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Paper>
    )
}

export default RevenueAllocationWidget
